// Keeps a page's IndexedDB copy of /data or /customers current without downloading the
// whole root. A Cloud Function (functions/changeFeed.js) writes /changes/{root}/{id} = the
// server time of each record's last write or removal. A page catches up on what changed since
// its last visit (catchUp), then listens to that small index and fetches only the records
// named in it (watch). Listening to the root itself made Firebase send all of it (MBs) on
// every load.
//
// The mark `${store}FeedSince` (IndexedDB metadata) is a /changes time: every change before it
// is in the copy. Records are fetched again freely (replays write the same data).
import { ref, query, orderByValue, startAt, limitToLast, get, onChildAdded, onChildChanged } from 'firebase/database';
import { database } from './initFirebase';
import { getDB } from './utilities';

// More changes than this since the last visit: one full download is cheaper than fetching each.
const FULL_RELOAD_OVER = 300;
// Changes stamped a little before the mark are fetched again, in case they arrived out of order.
const MARGIN_MS = 5000;

const markKey = store => `${store}FeedSince`;
const caught = {}; // root → { since, seen } from this page's last catch-up

async function readMark(store) {
  const db = await getDB();
  return new Promise(resolve => {
    const req = db.transaction('metadata', 'readonly').objectStore('metadata').get(markKey(store));
    req.onsuccess = () => resolve(req.result?.timestamp || 0);
    req.onerror = () => resolve(0);
  });
}

async function writeMark(store, timestamp) {
  const db = await getDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction('metadata', 'readwrite');
    const st = tx.objectStore('metadata');
    const req = st.get(markKey(store));
    // Never move the mark back (two pages may be watching).
    req.onsuccess = () => { if ((req.result?.timestamp || 0) < timestamp) st.put({ key: markKey(store), timestamp }); };
    tx.oncomplete = resolve;
    tx.onerror = e => reject(e.target?.error || e);
  });
}

/** Writes fetched records ([id, value | null]) into the store; null removes. */
async function applyRecords(store, records) {
  if (!records.length) return;
  const db = await getDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const st = tx.objectStore(store);
    records.forEach(([id, val]) => (val && typeof val === 'object' ? st.put({ id, ...val }) : st.delete(id)));
    tx.oncomplete = resolve;
    tx.onerror = e => reject(e.target?.error || e);
    tx.onabort = e => reject(e.target?.error || e);
  });
}

const latestChange = async root => {
  let at = 0;
  (await get(query(ref(database, `changes/${root}`), orderByValue(), limitToLast(1)))).forEach(c => { at = c.val(); });
  return at;
};

/**
 * Brings the store up to date. `full()` is the page's own whole download, used the first
 * time on a device (no mark yet) and when too much changed meanwhile. Resolves to the ids
 * fetched record by record, or null after a full download. Needs a connection.
 */
export async function catchUp({ root, store, full }) {
  const since = await readMark(store);
  if (since) {
    const entries = [];
    (await get(query(ref(database, `changes/${root}`), orderByValue(), startAt(since - MARGIN_MS)))).forEach(c => { entries.push([c.key, c.val()]); });
    if (entries.length <= FULL_RELOAD_OVER) {
      const records = await Promise.all(entries.map(([id]) => get(ref(database, `${root}/${id}`)).then(s => [id, s.val()])));
      await applyRecords(store, records);
      const until = Math.max(since, ...entries.map(([, at]) => at));
      await writeMark(store, until);
      caught[root] = { since: until, seen: new Map(entries) };
      return entries.map(([id]) => id);
    }
  }
  // Every change after the newest one in the index right now is fetched by the feed afterwards.
  const mark = (await latestChange(root)) || 1;
  await full();
  await writeMark(store, mark);
  caught[root] = { since: mark, seen: new Map() };
  return null;
}

/**
 * Calls `onChange(id, value | null)` for each record written or removed after this page's
 * catch-up (a page that didn't catch up, like one in the office shell's iframe, hears changes
 * from now on). One at a time, in the order they came. The page's handler updates its store
 * and screen; null means the record is gone.
 */
export async function watch({ root, store, onChange }) {
  const from = caught[root] || { since: (await latestChange(root)) || 1, seen: new Map() };
  const seen = new Map(from.seen);
  let chain = Promise.resolve();
  let markTimer;
  let markAt = 0;
  const handle = snap => {
    const id = snap.key;
    const at = snap.val();
    if (typeof at !== 'number' || (seen.get(id) || 0) >= at) return;
    seen.set(id, at);
    chain = chain.then(async () => {
      const rec = await get(ref(database, `${root}/${id}`));
      await onChange(id, rec.exists() ? rec.val() : null);
      markAt = Math.max(markAt, at);
      clearTimeout(markTimer);
      markTimer = setTimeout(() => writeMark(store, markAt).catch(() => {}), 2000);
    }).catch(err => console.error(`[changeFeed] ${root}/${id} update failed`, err));
  };
  const q = query(ref(database, `changes/${root}`), orderByValue(), startAt(Math.max(1, from.since - MARGIN_MS)));
  onChildAdded(q, handle);
  onChildChanged(q, handle);
}
